// React's lazy/Suspense are used inside pages/forge/Interest.tsx now; App.tsx doesn't need them directly.
import type { ReactElement } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import AskPanel from "./components/ai/AskPanel";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { ThemeProvider } from "./hooks/useTheme";
import { ToastProvider } from "./hooks/useToast";
import { ErrorBoundary } from "./components/ErrorBoundary";
import Login from "./pages/Login";
import SyncDashboard from "./pages/SyncDashboard";
import Dashboard from "./pages/Dashboard";
import OutstandingWork from "./pages/OutstandingWork";
import BodBrief from "./pages/BodBrief";
import CrossOrgStatus from "./pages/CrossOrgStatus";
import StrategicReviews from "./pages/StrategicReviews";
import Finance from "./pages/Finance";
import FinanceOverview from "./pages/FinanceOverview";
import FinanceHistory from "./pages/FinanceHistory";
import FinanceFY27V2 from "./pages/FinanceFY27V2";
import FinanceDeficit from "./pages/FinanceDeficit";
import FinanceProjects from "./pages/FinanceProjects";
import FinanceVendors from "./pages/FinanceVendors";
import FinancePurchaseOrders from "./pages/FinancePurchaseOrders";
import FinanceBoardPacket from "./pages/FinanceBoardPacket";
import FinanceAlerts from "./pages/FinanceAlerts";
import FinanceApprovals from "./pages/FinanceApprovals";
import FinanceNonProfit from "./pages/FinanceNonProfit";
import ApplicantTracker from "./pages/ApplicantTracker";
import RetentionAnalysis from "./pages/RetentionAnalysis";
import EquipmentGear from "./pages/EquipmentGear";
import EmsSupply from "./pages/EmsSupply";
import IronsIssues from "./pages/IronsIssues";
import FleetStatus from "./pages/FleetStatus";
import MemberEngagement from "./pages/MemberEngagement";
import CommunityEngagement from "./pages/CommunityEngagement";
import Staffing from "./pages/Staffing";
import StaffingCapacity from "./pages/StaffingCapacity";
import ForgeInterest from "./pages/forge/Interest";
// ForgeRecruitment + ForgeAssignment are now wrapped by Application + YearOneActive;
// their component files stay but are no longer imported directly from App.
import ForgeTraining from "./pages/forge/Training";
import ForgePipelineOverview from "./pages/forge/PipelineOverview";
import ForgeApplication from "./pages/forge/Application";
import ForgeBODVote from "./pages/forge/BODVote";
import ForgeYearOneActive from "./pages/forge/YearOneActive";
import ForgeInterventions from "./pages/forge/Interventions";
import ForgeOutreachSignals from "./pages/forge/OutreachSignals";
import ForgeApplicantForecast from "./pages/forge/ApplicantForecast";
import RetentionRisk from "./pages/RetentionRisk";
import ExternalSignals from "./pages/ExternalSignals";
import Interview from "./pages/feedback/Interview";
import RootCauseAnalysis from "./pages/RootCauseAnalysis";
import ViabilityScenarios from "./pages/ViabilityScenarios";
import UserManagement from "./pages/UserManagement";
import AdminAI from "./pages/AdminAI";
import InstitutionalMemory from "./pages/InstitutionalMemory";
// Viability page retired — /viability now 301s to /viability/scenarios.
import Layout from "./components/Layout";
import "./index.css";

// Geography is lazy-loaded inside pages/forge/Interest.tsx as a tab.

function LoadingScreen() {
  return (
    <div className="min-h-dvh flex items-center justify-center bg-theme-surface text-theme-primary">
      <div className="text-center">
        <img
          src="/dcvfd-badge.svg"
          alt="DCVFD"
          width={48}
          height={48}
          className="mx-auto mb-4 opacity-80 animate-pulse"
        />
        <p className="text-theme-muted text-sm">Loading...</p>
      </div>
    </div>
  );
}

/**
 * Pure decision for a permission route guard. Extracted (and unit-tested in
 * App.guard.test.ts) so the loading-race fix is locked in:
 *
 *   - "wait"     → auth state is still resolving (isLoading, or user not yet
 *                  populated). Render the loading screen; DO NOT decide yet.
 *   - "deny"     → auth settled and the permission is false → redirect home.
 *   - "allow"    → auth settled and the permission is true  → render children.
 *
 * Permissions are resolved server-side and arrive asynchronously via
 * /api/auth/me. Reading `granted` while auth is still loading would see
 * `undefined` and wrongly bounce a correctly-permissioned user (e.g. a viewer-
 * role 'employees'-group staffer) to "/". AuthGate already gates the AppShell
 * today, but each guard must be self-defending and not rely on that invariant.
 */
export function guardDecision(
  isLoading: boolean,
  user: { permissions: Record<string, boolean> } | null,
  permissionKey: string,
): "wait" | "deny" | "allow" {
  if (isLoading || !user) return "wait";
  return user.permissions[permissionKey] ? "allow" : "deny";
}

function PermissionGuard({
  permissionKey,
  children,
}: {
  permissionKey: string;
  children: ReactElement;
}) {
  const { user, isLoading } = useAuth();
  const decision = guardDecision(isLoading, user, permissionKey);
  if (decision === "wait") return <LoadingScreen />;
  if (decision === "deny") return <Navigate to="/" replace />;
  return children;
}

/**
 * Client guard for the /finance/* surface. Mirrors the server-side
 * view_finance permission (resolved in /api/auth/me). Viewer-role paid staff in
 * the Entra 'employees' group pass; general members are redirected home. The API
 * still enforces requirePermission('view_finance') regardless — this only keeps
 * the UI honest (no flash of a forbidden page).
 */
function RequireFinance({ children }: { children: ReactElement }) {
  return (
    <PermissionGuard permissionKey="view_finance">{children}</PermissionGuard>
  );
}

/**
 * Client guard for member-PII pages (Member Engagement, Retention). Mirrors the
 * server-side view_members permission (resolved in /api/auth/me): board/senior
 * staff/employees pass, the general membership is redirected home. The API still
 * enforces requirePermission('view_members') — this only keeps the UI honest and
 * prevents a viewer from navigating to a page whose data would 403. S-CLIENT.
 */
function RequireMembers({ children }: { children: ReactElement }) {
  return (
    <PermissionGuard permissionKey="view_members">{children}</PermissionGuard>
  );
}

/** Client guard for applicant-PII pages. Mirrors view_applicants. S-CLIENT. */
function RequireApplicants({ children }: { children: ReactElement }) {
  return (
    <PermissionGuard permissionKey="view_applicants">
      {children}
    </PermissionGuard>
  );
}

/**
 * Client guard for admin-only surfaces (Admin AI console). Mirrors the
 * server-side requireRole('admin') gates on /api/admin/ai/*. Non-admins are
 * redirected home instead of rendering the page chrome and then 403-ing on
 * every fetch. The API still enforces the role — this only keeps the UI honest.
 * S-CLIENT.
 */
function RequireAdmin({ children }: { children: ReactElement }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen />;
  if (user?.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function AppShell() {
  const { user } = useAuth();

  return (
    <Layout role={user!.role}>
      <AskPanel />
      <Routes>
        <Route path="/" element={<Dashboard />} />
        {/* FORGE pipeline — canonical routes */}
        <Route path="/forge" element={<ForgePipelineOverview />} />
        <Route path="/forge/interest" element={<ForgeInterest />} />
        <Route
          path="/forge/geography"
          element={<Navigate to="/forge/interest?tab=geography" replace />}
        />
        <Route path="/forge/application" element={<ForgeApplication />} />
        <Route path="/forge/bod-vote" element={<ForgeBODVote />} />
        <Route path="/forge/training" element={<ForgeTraining />} />
        <Route path="/forge/year-one" element={<ForgeYearOneActive />} />
        <Route path="/forge/interventions" element={<ForgeInterventions />} />
        <Route
          path="/forge/outreach-signals"
          element={<ForgeOutreachSignals />}
        />
        <Route path="/forge/forecast" element={<ForgeApplicantForecast />} />
        <Route path="/external-signals" element={<ExternalSignals />} />
        {/* Backward-compat redirects for old routes */}
        <Route
          path="/forge/weekly-report"
          element={<Navigate to="/forge" replace />}
        />
        <Route
          path="/forge/recruitment"
          element={<Navigate to="/forge/application" replace />}
        />
        <Route
          path="/forge/assignment"
          element={<Navigate to="/forge/year-one" replace />}
        />
        <Route
          path="/recruitment"
          element={<Navigate to="/forge/application" replace />}
        />
        <Route
          path="/training"
          element={<Navigate to="/forge/training" replace />}
        />
        {/* Other FORGE pages */}
        <Route
          path="/applicants"
          element={
            <RequireApplicants>
              <ApplicantTracker />
            </RequireApplicants>
          }
        />
        <Route
          path="/retention"
          element={
            <RequireMembers>
              <RetentionAnalysis />
            </RequireMembers>
          }
        />
        <Route path="/retention/root-cause" element={<RootCauseAnalysis />} />
        <Route
          path="/retention/risk"
          element={
            <RequireMembers>
              <RetentionRisk />
            </RequireMembers>
          }
        />
        {/* Operations */}
        <Route path="/staffing" element={<Staffing />} />
        <Route path="/ops/staffing-capacity" element={<StaffingCapacity />} />
        <Route path="/sync" element={<SyncDashboard />} />
        <Route path="/outstanding" element={<OutstandingWork />} />
        <Route path="/bod" element={<BodBrief />} />
        <Route path="/cross-org" element={<CrossOrgStatus />} />
        <Route path="/strategic" element={<StrategicReviews />} />
        <Route
          path="/finance"
          element={
            <RequireFinance>
              <Finance />
            </RequireFinance>
          }
        />
        <Route
          path="/finance/levy"
          element={
            <RequireFinance>
              <Finance />
            </RequireFinance>
          }
        />
        <Route
          path="/finance/non-profit"
          element={
            <RequireFinance>
              <FinanceNonProfit />
            </RequireFinance>
          }
        />
        {/* Back-compat: the old "Non-Levy Funds" coming-soon link now lands on the Non-Profit dashboard. */}
        <Route
          path="/finance/non-levy"
          element={<Navigate to="/finance/non-profit" replace />}
        />
        <Route
          path="/finance/overview"
          element={
            <RequireFinance>
              <FinanceOverview />
            </RequireFinance>
          }
        />
        {/* Retired v1 committee-budget builder (dash_finance_committee_budget) —
            superseded by the FY27 v2 restructure. Redirect old bookmarks/links
            rather than 404 them. */}
        <Route
          path="/finance/budget"
          element={<Navigate to="/finance/fy27-v2" replace />}
        />
        <Route
          path="/finance/committees"
          element={<Navigate to="/finance/fy27-v2" replace />}
        />
        {/* Old v1 FY27 Budget page replaced by v2 — both routes render v2. */}
        <Route
          path="/finance/fy27-budget"
          element={
            <RequireFinance>
              <FinanceFY27V2 />
            </RequireFinance>
          }
        />
        <Route
          path="/finance/fy27-v2"
          element={
            <RequireFinance>
              <FinanceFY27V2 />
            </RequireFinance>
          }
        />
        <Route
          path="/finance/deficit"
          element={
            <RequireFinance>
              <FinanceDeficit />
            </RequireFinance>
          }
        />
        <Route
          path="/finance/projects"
          element={
            <RequireFinance>
              <FinanceProjects />
            </RequireFinance>
          }
        />
        <Route
          path="/finance/history"
          element={
            <RequireFinance>
              <FinanceHistory />
            </RequireFinance>
          }
        />
        <Route
          path="/finance/vendors"
          element={
            <RequireFinance>
              <FinanceVendors />
            </RequireFinance>
          }
        />
        <Route
          path="/finance/purchase-orders"
          element={
            <RequireFinance>
              <FinancePurchaseOrders />
            </RequireFinance>
          }
        />
        <Route
          path="/finance/board-packet"
          element={
            <RequireFinance>
              <FinanceBoardPacket />
            </RequireFinance>
          }
        />
        <Route
          path="/finance/alerts"
          element={
            <RequireFinance>
              <FinanceAlerts />
            </RequireFinance>
          }
        />
        <Route
          path="/finance/approvals"
          element={
            <RequireFinance>
              <FinanceApprovals />
            </RequireFinance>
          }
        />
        <Route path="/equipment" element={<EquipmentGear />} />
        <Route path="/ems-supply" element={<EmsSupply />} />
        <Route path="/irons" element={<IronsIssues />} />
        <Route path="/fleet" element={<FleetStatus />} />
        <Route
          path="/membership"
          element={
            <RequireMembers>
              <MemberEngagement />
            </RequireMembers>
          }
        />
        <Route path="/community" element={<CommunityEngagement />} />
        <Route path="/users" element={<UserManagement />} />
        <Route
          path="/admin/ai"
          element={
            <RequireAdmin>
              <AdminAI />
            </RequireAdmin>
          }
        />
        <Route path="/memory" element={<InstitutionalMemory />} />
        {/* DATA-* — /viability redirects to the Monte Carlo scenario bands.
            The 60-year point forecast was epistemically overclaiming; the
            10-year band view is the honest version. */}
        <Route
          path="/viability"
          element={<Navigate to="/viability/scenarios" replace />}
        />
        <Route path="/viability/scenarios" element={<ViabilityScenarios />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function AuthGate() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  return <AppShell />;
}

/**
 * Public routes that bypass AuthGate. The /feedback/<token> URL is the
 * stay/exit interview page — the token IS the auth, so the recipient
 * doesn't need a DCVFD login. Anything matching here is rendered without
 * requiring an authenticated session.
 */
function PublicOrAuthed() {
  return (
    <Routes>
      <Route path="/feedback/:token" element={<Interview />} />
      <Route path="*" element={<AuthGate />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <ToastProvider>
              <PublicOrAuthed />
            </ToastProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
